import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const Home = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Infrastructure for human-centered systems</h1>
            <p className="hero-subtitle">
              Pathway Labs builds compliance-first apps, data pipelines, and AI governance
              for programs that serve families, youth, and communities.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="btn btn-primary btn-large">Get Started</Link>
              <Link to="/platform" className="btn btn-secondary btn-large">Explore the Platform</Link>
            </div>
            <p className="hero-trust-line">
              Built for nonprofits, schools, social impact ventures, and mission-aligned capital.
            </p>
          </div>
        </div>
      </section>

      {/* Value Pillars Section */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What Pathway Labs Delivers</span>
          </div>
          <div className="grid-3">
            <div className="pillar-card">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                </svg>
              </div>
              <h3 className="pillar-title">Apps & Workflows</h3>
              <p className="pillar-subtitle">Tooling for programs, case management, training, and internal operations.</p>
              <p className="pillar-text">
                We replace scattered spreadsheets and one-off tools with connected, reliable systems.
              </p>
            </div>

            <div className="pillar-card">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="M18 9l-5 5-4-4-3 3" />
                </svg>
              </div>
              <h3 className="pillar-title">Data & Evidence Layer</h3>
              <p className="pillar-subtitle">A unified data model that ties people, activities, and outcomes together.</p>
              <p className="pillar-text">
                You get clean reporting for grants, partners, boards, and internal decision-making.
              </p>
            </div>

            <div className="pillar-card">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <h3 className="pillar-title">AI Governance</h3>
              <p className="pillar-subtitle">Role-based access, policy rules, and event logging built into the platform.</p>
              <p className="pillar-text">
                We help you use AI safely and responsibly across sensitive environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Built for real organizations</span>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              We partner with mission-critical organizations that require secure, stable, and compliant systems.
            </p>
          </div>
          <div className="grid-4">
            <div className="card">
              <h4 className="card-title">Nonprofits & Foundations</h4>
              <p className="card-text">
                Modern systems for program tracking, grant compliance, and donor transparency.
              </p>
            </div>
            <div className="card">
              <h4 className="card-title">Education & Youth Programs</h4>
              <p className="card-text">
                Infrastructure for youth services, schools, and community programs that need secure, trauma-aware systems.
              </p>
            </div>
            <div className="card">
              <h4 className="card-title">Social Impact Ventures</h4>
              <p className="card-text">
                A launchpad for teams building new products in education, housing, family services, or financial resilience.
              </p>
            </div>
            <div className="card">
              <h4 className="card-title">Mission-Aligned Capital & Family Offices</h4>
              <p className="card-text">
                Data, reporting, and governance layers that align capital with long-term impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="section-label">How we work with you</span>
          </div>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4 className="step-title">Discovery</h4>
                <p className="step-text">We learn your programs, constraints, and existing tools.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4 className="step-title">System Design</h4>
                <p className="step-text">We map your workflows into a clear architecture: apps, data, and governance.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4 className="step-title">Deployment</h4>
                <p className="step-text">We roll out applications, integrations, and dashboards in phases so your team can adopt smoothly.</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4 className="step-title">Continuity & Support</h4>
                <p className="step-text">We maintain and evolve the system as your organization grows, adds programs, or changes leadership.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="section ecosystem-section">
        <div className="container">
          <div className="ecosystem-content">
            <span className="section-label">Part of the Guardian ecosystem</span>
            <p className="ecosystem-text">
              Pathway Labs is the infrastructure arm within the broader Guardian ecosystem,
              supporting programs focused on families, youth, housing, and education.
            </p>
            <p className="ecosystem-text">
              We design systems that can serve local pilots today and multi-generation missions tomorrow.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to modernize your infrastructure?</h2>
            <p className="cta-text">
              Tell us about your organization and where you're stuck. We'll help you map a clear path forward.
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">Get Started</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
